import { Label } from '@fluentui/react';
import * as React from 'react';
import { Field } from 'react-final-form';
import { connect } from 'react-redux';
import { getLocaleText } from 'src/common/utilities/Utilities';
import Text from 'src/components/text/Text';
import { getLocalizedOrganizationTypeText } from './ProfileSharedFunctions';
import ResourceService from 'src/dataServices/ResourceService';
import { IApplicationState } from 'src/state';
import { IFeatureFlagsState } from 'src/state/feature-flags';
import { IAddressField, IAddressResources, IFieldItem, IProfile } from 'src/state/profile/types';
import { ICountryCode } from 'src/state/shared';
import { parseTextWithUnderline } from '../utilities/Helpers/parseTextWithUnderline';
import FieldValidationError from './FieldValidationError';
import './ProfileForm.scss';
import {
  isBannedAbbreviationValidation,
  composePhoneNumberValidator,
  composeRequiredFieldValidator,
  composeWebsiteUrlValidator,
  getPostalCodeValidator,
  hasEmojisValidation,
  minLengthValidation,
  onlySpacesOrTabsValidation,
  onlySpecialCharactersValidation,
  requiredFieldValidator,
  hasBannedOrgNameInputValidation,
  startsOrEndsWithSpaceValidation,
  hasNonprintableCharactersValidation,
  isNumericValidation
} from './ProfileV2SharedComponents';
import TextInputV2 from './TextInputV2';

interface IPropsFromState {
  featureFlags: IFeatureFlagsState;
}

interface IPropsFromParent {
  profile: IProfile;
  profileForm: any;
  onFieldChange: (field: string, value: string, preserveOrganizationType?: boolean) => void;
}

type IProfileProps = IPropsFromParent & IPropsFromState;

interface IOrganizationTypeOption {
  key: string;
  text: string;
}

const mapOrganizationTypeOptions = (organizationTypes: unknown): IOrganizationTypeOption[] => {
  if (!Array.isArray(organizationTypes)) {
    return [];
  }

  return organizationTypes.map((organizationType) => {
    const key = String(organizationType);

    return {
      key,
      text: getLocalizedOrganizationTypeText(key)
    };
  });
};

const OrgInfoSection2Edit = ({ profile, profileForm, onFieldChange, featureFlags }: IProfileProps): JSX.Element => {
  const [countries, setCountries] = React.useState<ICountryCode[]>([]);
  const [organizationTypes, setOrganizationTypes] = React.useState<IOrganizationTypeOption[]>([]);
  const [selectedCountryCode, setSelectedCountryCode] = React.useState<string>(profile.address.countryCode);
  const [addressResources, setAddressResources] = React.useState<IAddressResources>();
  const [stateField, setStateField] = React.useState<IAddressField | null>(null);
  const profileFormRef = React.useRef(profileForm);
  const onFieldChangeRef = React.useRef(onFieldChange);
  const isInitialCountryLoad = React.useRef(true);
  const tradescreeningPhaseTwoEnabled = featureFlags.tradescreeningPhaseTwoEnabled;
  const orgNameErrorMessage = getLocaleText("ProfileV2", "orgName.Error");

  React.useEffect(() => {
    profileFormRef.current = profileForm;
    onFieldChangeRef.current = onFieldChange;
  }, [profileForm, onFieldChange]);

  React.useEffect(() => {
    async function fetchAddressResourceData() {
      const response = await ResourceService.getAddressResourcesForCountry(selectedCountryCode);
      setAddressResources(response.value);
    }

    fetchAddressResourceData();
  }, [selectedCountryCode]);

  React.useEffect(() => {
    let isMounted = true;
    const isFirstLoad = isInitialCountryLoad.current;
    isInitialCountryLoad.current = false;

    const fetchStateFieldData = async () => {
      try {
        const response = await ResourceService.getAddressFields(selectedCountryCode);
        if (!isMounted) return;

        const found = response.value?.addressFields?.find(
          (f) => f.fieldID === 'StateRegion' || f.fieldID === 'State'
        ) ?? null;

        setStateField(found);

        // On country change (not initial load): clear state value if not valid for new options
        if (!isFirstLoad && found?.fieldItems?.length) {
          const currentStateValue: string = profileFormRef.current.getState().values?.address?.stateRegion ?? '';
          const isValueValid = found.fieldItems.some((item) => item.value === currentStateValue);
          if (currentStateValue && !isValueValid) {
            profileFormRef.current.change('address.stateRegion', '');
            onFieldChangeRef.current('address.stateRegion', '');
          }
        }
      } catch {
        if (isMounted) {
          setStateField(null);
        }
      }
    };

    fetchStateFieldData();

    return () => {
      isMounted = false;
    };
  }, [selectedCountryCode]);

  React.useEffect(() => {
    ResourceService.getCountryCodes()
      .then((data) => setCountries(data.value as ICountryCode[]))
      .catch((error) => console.error(error));
  }, []);

  React.useEffect(() => {
    let isMounted = true;

    const fetchOrganizationTypeData = async () => {
      try {
        const response = await ResourceService.getOrganizationTypes(selectedCountryCode);
        
        // Extract orgTypes array from response structure: { countryCode, orgTypes: [...] }
        const orgTypesArray = (response.value as any)?.orgTypes || [];
        const organizationTypeOptions = mapOrganizationTypeOptions(orgTypesArray);

        if (!isMounted) {
          return;
        }

        setOrganizationTypes(organizationTypeOptions);

        const selectedOrganizationType = profileFormRef.current.getState().values.organizationType;
        const selectedValueIsValid = organizationTypeOptions.some((organizationType) => {
          return organizationType.key === selectedOrganizationType;
        });

        // If organization type is set but no longer valid for this country, clear it
        if (selectedOrganizationType && !selectedValueIsValid) {
          profileFormRef.current.change('organizationType', '');
          onFieldChangeRef.current('organizationType', '');
        }
      } catch {
        if (isMounted) {
          setOrganizationTypes([]);
        }
      }
    };

    fetchOrganizationTypeData();

    return () => {
      isMounted = false;
    };
  }, [selectedCountryCode]);

  return (
    <div id="org-info">
      <div id="profile-org-info-description">
        <p>{getLocaleText('ProfileV2', 'OrgInformationDescription')}</p>
      </div>
      <div className="ms-Grid-row">
        <div className="ms-Grid-col ms-sm12 ms-md4 ms-lg4 ms-xl4">
          <TextInputV2
            id="name"
            localeCategoryId="ProfileV2"
            localeLabelId="OrganizationName"
            localeErrorCategoryId="ProfileV2"
            required={true}
            autoFocus={true}
            maxLength={tradescreeningPhaseTwoEnabled ? 220 : 200}
            validators={
              tradescreeningPhaseTwoEnabled
              ? [
                composeRequiredFieldValidator('name'),
                minLengthValidation(orgNameErrorMessage, 2),
                onlySpecialCharactersValidation(orgNameErrorMessage),
                onlySpacesOrTabsValidation(orgNameErrorMessage),
                isNumericValidation(orgNameErrorMessage),
                isBannedAbbreviationValidation(orgNameErrorMessage),
                hasBannedOrgNameInputValidation(orgNameErrorMessage),
                hasEmojisValidation(orgNameErrorMessage),
                startsOrEndsWithSpaceValidation(orgNameErrorMessage),
                hasNonprintableCharactersValidation(orgNameErrorMessage),
              ]
              : [composeRequiredFieldValidator('name')]
            }
            tooltipContent={
              tradescreeningPhaseTwoEnabled
              ? <ul className="tooltip-list">
                <li>{parseTextWithUnderline(getLocaleText('ProfileV2', 'orgName.lengthValidation'))}</li>
                <li>{parseTextWithUnderline(getLocaleText('ProfileV2', 'orgName.specialCharactersValidation'))}</li>
                <li>{parseTextWithUnderline(getLocaleText('ProfileV2', 'orgName.spacesValidation'))}</li>
                <li>{parseTextWithUnderline(getLocaleText('ProfileV2', 'orgName.numbersOnlyValidation'))}</li>
                <li>{getLocaleText('ProfileV2', 'orgName.abbreviationValidation')}</li>
                <li>{getLocaleText('ProfileV2', 'orgName.maxLengthValidation')}</li>
                <li>{getLocaleText('ProfileV2', 'orgName.emojisValidation')}</li>
                <li>{getLocaleText('ProfileV2', 'orgName.nonPrintableValidation')}</li>
                <li>{getLocaleText('ProfileV2', 'orgName.internetDomainValidation')}</li>
                <li>{getLocaleText('ProfileV2', 'orgName.sequenceValidation')}</li>
              </ul>
              : undefined
            }
            iconName="info"
          />
        </div>
        {featureFlags.profilePageEnhancementsEnabled && (
          <div className="ms-Grid-col ms-sm12 ms-md4 ms-lg4 ms-xl4">
            <Label tabIndex={-1} htmlFor="organizationType">
              * 
              <Text category="ProfileV2" id="OrganizationType" />
            </Label>
            <Field
              name="organizationType"
              id="organizationType"
              component={renderOrganizationTypesDropdown}
              props={{
                organizationTypes,
                onFieldChange
              }}
              validate={composeRequiredFieldValidator('organizationType')}
            />
          </div>
        )}
      </div>
      <div className="ms-Grid-row mt-3">
        <div className="ms-Grid-col ms-sm12 ms-md4 ms-lg4 ms-xl4">
          <TextInputV2
            id="address.addressLine1"
            localeCategoryId="ProfileV2"
            localeLabelId="StreetAddress1"
            localeErrorCategoryId="ProfileV2"
            required={true}
            validators={[composeRequiredFieldValidator('address.addressLine1')]}
          />
        </div>
        <div className="ms-Grid-col ms-sm12 ms-md4 ms-lg4 ms-xl4">
          <TextInputV2
            id="address.addressOther"
            localeCategoryId="ProfileV2"
            localeLabelId="StreetAddress2"
            required={false}
          />
        </div>
        <div className="ms-Grid-col ms-sm12 ms-md4 ms-lg4 ms-xl4">
          <TextInputV2
            id="address.city"
            localeCategoryId="Profile"
            localeLabelId="City"
            required={true}
            validators={[composeRequiredFieldValidator('address.city')]}
          />
        </div>
      </div>
      <div className="ms-Grid-row mt-3">
        <div className="ms-Grid-col ms-sm12 ms-md4 ms-lg4 ms-xl4">
          {stateField?.fieldItems?.length ? (
            <>
              <Label tabIndex={-1} htmlFor="address.stateRegion">
                {stateField.required && <span aria-hidden="true">* </span>}
                <Text category="Profile" id="State" />
              </Label>
              <Field
                name="address.stateRegion"
                id="address.stateRegion"
                component={renderStateDropdown}
                props={{
                  stateFieldItems: stateField.fieldItems,
                  onFieldChange
                }}
                validate={stateField.required ? composeRequiredFieldValidator('address.stateRegion') : undefined}
              />
            </>
          ) : (
            <TextInputV2
              id="address.stateRegion"
              localeCategoryId="Profile"
              localeLabelId="State"
              localeErrorCategoryId="ProfileV2"
              required={stateField ? stateField.required : true}
              validators={
                (stateField ? stateField.required : true)
                  ? [composeRequiredFieldValidator('address.stateRegion')]
                  : []
              }
            />
          )}
        </div>
        <div className="ms-Grid-col ms-sm12 ms-md4 ms-lg4 ms-xl4">
          <TextInputV2
            id="address.postalCode"
            localeCategoryId="Profile"
            localeLabelId="Postcode"
            required={true}
            validators={[
              composeRequiredFieldValidator('address.postalCode'),
              getPostalCodeValidator(addressResources as IAddressResources)
            ]}
          />
        </div>
        <div className="ms-Grid-col ms-sm12 ms-md4 ms-lg4 ms-xl4">
          <Label tabIndex={-1} htmlFor="address.countryCode">
            <span aria-hidden="true">* </span>
            <Text category="Profile" id="Country" />
          </Label>
          <Field
            name="address.countryCode"
            id="address.countryCode"
            component={renderCountriesDropdown}
            props={{
              countries: countries,
              setSelectedCountryCode: setSelectedCountryCode,
              onFieldChange: onFieldChange,
              disableCountryEdit: profile.disableCountryEdit
            }}
            validate={(value) => requiredFieldValidator(value, 'address.countryCode')}
          />
        </div>
      </div>
      <div className="ms-Grid-row mt-3">
        <div className="ms-Grid-col ms-sm12 ms-md4 ms-lg4 ms-xl4">
          <TextInputV2
            id="websiteUrl"
            localeCategoryId="Profile"
            localeLabelId="Website"
            required={true}
            validators={[composeRequiredFieldValidator('websiteUrl'), composeWebsiteUrlValidator('websiteUrl')]}
          />
        </div>
        <div className="ms-Grid-col ms-sm12 ms-md4 ms-lg4 ms-xl4">
          <TextInputV2
            id="address.phone"
            localeCategoryId="ProfileV2"
            localeLabelId="PhoneNumber"
            localeErrorCategoryId="ProfileV2"
            required={true}
            validators={[composeRequiredFieldValidator('address.phone'), composePhoneNumberValidator('address.phone')]}
          />
        </div>
      </div>
    </div>
  );
};

interface IrenderStateDropdown {
  input: any;
  meta: { touched: boolean; error: string };
  props: {
    stateFieldItems: IFieldItem[];
    onFieldChange: any;
  };
}

const renderStateDropdown = ({
  input,
  meta: { touched, error },
  props: { stateFieldItems, onFieldChange }
}: IrenderStateDropdown) => {
  return (
    <div>
      <div className="custom-select">
        <select
          className="custom-select-input"
          onBlur={input.onBlur}
          onChange={(e) => {
            input.onChange(e);
            onFieldChange(input.name, e.target.value);
          }}
          value={input.value}
          aria-label={
            input.value === ''
              ? getLocaleText('Profile', 'State')
              : input.value
          }
        >
          <option value="">{getLocaleText('Profile', 'State')}</option>
          {stateFieldItems.map((item) => (
            <option key={item.value} value={item.value}>
              {item.label}
            </option>
          ))}
        </select>
        <span className="custom-select-arrow" aria-hidden="true" />
      </div>
      {touched && error && <FieldValidationError localeErrorTextId={error} />}
    </div>
  );
};

interface IrenderCountriesDropdown {
  input: any;
  meta: { touched: boolean; error: string };
  props: {
    countries: ICountryCode[];
    setSelectedCountryCode: any;
    onFieldChange: any;
    disableCountryEdit: boolean;
  };
}

interface IrenderOrganizationTypesDropdown {
  input: any;
  meta: { touched: boolean; error: string };
  props: {
    organizationTypes: IOrganizationTypeOption[];
    onFieldChange: any;
  };
}

const renderOrganizationTypesDropdown = ({
  input,
  meta: { touched, error },
  props: { organizationTypes, onFieldChange }
}: IrenderOrganizationTypesDropdown) => {
  return (
    <div>
      <div className="custom-select">
        <select
          className="custom-select-input"
          required
          onBlur={input.onBlur}
          onChange={(e) => {
            input.onChange(e);
            onFieldChange(input.name, e.target.value, false);
          }}
          value={input.value}
          aria-label={
            input.value === ''
              ? getLocaleText('ProfileV2', 'OrganizationTypePlaceholder')
              : input.value
          }
        >
          <option value="">{getLocaleText('ProfileV2', 'OrganizationTypePlaceholder')}</option>
          {(organizationTypes || []).map((organizationType) => (
            <option key={organizationType.key} value={organizationType.key}>
              {organizationType.text}
            </option>
          ))}
        </select>
        <span className="custom-select-arrow" aria-hidden="true" />
      </div>
      {touched && error && <FieldValidationError localeErrorTextId={error} />}
    </div>
  );
};

const renderCountriesDropdown = ({
  input,
  meta: { touched, error },
  props: { countries, setSelectedCountryCode, onFieldChange, disableCountryEdit }
}: IrenderCountriesDropdown) => {
  return (
    <div>
      <div className="custom-select">
        <select
          className="custom-select-input"
          required
          onBlur={input.onBlur}
          onChange={(e) => {
            input.onChange(e);
            setSelectedCountryCode(e.target.value);
            onFieldChange(input.name, e.target.value);
          }}
          value={input.value}
          aria-label={input.value === '' ? getLocaleText('ProfileV2', `CountryPlaceholder`) : input.value}
          disabled={disableCountryEdit}
        >
          <option value="">{getLocaleText('ProfileV2', `CountryPlaceholder`)}</option>
          {(countries || []).map((countryCode, index) => (
            <option key={index} value={countryCode.code}>
              {countryCode.name + ' (' + countryCode.code + ')'}
            </option>
          ))}
        </select>
        <span className="custom-select-arrow" aria-hidden="true" />
      </div>
      {touched && error && <FieldValidationError localeErrorTextId={error} />}
    </div>
  );
};

const mapStateToProps = ({ flags }: IApplicationState) => ({
  featureFlags: flags
});

export default connect(mapStateToProps)(OrgInfoSection2Edit);